import api from './axiosConfig';
import { Access } from './sharingApi';

export interface DocumentDraft {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
  entries: DocumentDraftEntry[];
  entry_count: number;
}

export interface DocumentDraftEntry {
  id: number;
  content: string;
  order: number;
  tags: Array<{id: number; name: string; color: string; text_color: string}>;
  links: Array<{id: number; url: string; title: string}>;
}

export interface Document {
  id: number;
  access?: Access;
  name: string;
  content: string; // NEW: Document's own content
  folder_id?: number | null; // NEW: Folder assignment
  created_at: string;
  updated_at: string;
  source_entries: SourceEntry[]; // NEW: Info about original entries
  combined_tags: Array<{id: number; name: string; color: string; text_color: string}>;
  combined_links: Array<{id: number; url: string; title: string}>;
  combined_media: Array<{id: number; file: string; media_type: string}>;
  source_entry_count: number; // NEW: Count of source entries
}

export interface DocumentFolder {
  id: number;
  name: string;
  parent: number | null;
  created_at: string;
  updated_at: string;
  document_count: number;
}

export interface SourceEntry {
  entry_id: number;
  original_order: number;
  original_content: string;
  current_entry_content: string;
  has_diverged: boolean;
  tags: Array<{id: number; name: string; color: string; text_color: string}>;
  links: Array<{id: number; url: string; title: string}>;
}

// Document Draft API calls
export const createDocumentDraft = async (entryIds: number[], name: string): Promise<DocumentDraft> => {
  const response = await api.post('/document-drafts/', {
    entry_ids: entryIds,
    name: name
  });
  return response.data;
};

export const listDocumentDrafts = async (): Promise<DocumentDraft[]> => {
  const response = await api.get('/document-drafts/');
  return response.data;
};

export const getDocumentDraft = async (draftId: number): Promise<DocumentDraft> => {
  const response = await api.get(`/document-drafts/${draftId}/`);
  return response.data;
};

export const updateDocumentDraft = async (
  draftId: number, 
  updates: { name?: string; entry_orders?: Array<{id: number; order: number}> }
): Promise<void> => {
  await api.put(`/document-drafts/${draftId}/`, updates);
};

export const deleteDocumentDraft = async (draftId: number): Promise<void> => {
  await api.delete(`/document-drafts/${draftId}/`);
};

export const finalizeDocument = async (draftId: number, name?: string): Promise<Document> => {
  const response = await api.post(`/document-drafts/${draftId}/finalize/`, {
    name: name
  });
  return response.data;
};

// Document API calls
export const createBlankDocument = async (
  name: string,
  content: string = '',
  folderId: number | null = null
): Promise<Document> => {
  const response = await api.post('/documents/', {
    name: name,
    content: content,
    ...(folderId != null ? { folder: folderId } : {})
  });
  return response.data;
};

export const listDocuments = async (): Promise<Document[]> => {
  const response = await api.get('/documents/');
  return response.data;
};

export const getDocument = async (documentId: number): Promise<Document> => {
  const response = await api.get(`/documents/${documentId}/`);
  return response.data;
};

export const updateDocument = async (
  documentId: number,
  // NOTE: writes must use `folder` — the serializer treats `folder_id` as read-only.
  // base_updated_at is the updated_at this client last saw: the server answers
  // 409 rather than overwriting a version written by someone else since.
  updates: { name?: string; content?: string; folder?: number | null; base_updated_at?: string }
): Promise<Document> => {
  const response = await api.put(`/documents/${documentId}/`, updates);
  return response.data;
};

/**
 * Rename only. PATCH rather than the PUT updateDocument uses: `content` is a
 * required field on the serializer, so a PUT carrying just a name is rejected
 * — and one carrying a stale name/content pair would overwrite the document.
 */
export const renameDocument = async (documentId: number, name: string): Promise<Document> => {
  const response = await api.patch(`/documents/${documentId}/`, { name });
  return response.data;
};

export interface DocumentVersion {
  id: number;
  created_at: string;
  word_count: number;
  preview: string;
}

export const listDocumentVersions = async (
  documentId: number
): Promise<{ versions: DocumentVersion[] }> => {
  const response = await api.get(`/documents/${documentId}/versions/`);
  return response.data;
};

export const restoreDocumentVersion = async (
  documentId: number,
  versionId: number
): Promise<Document> => {
  const response = await api.post(`/documents/${documentId}/versions/${versionId}/restore/`);
  return response.data;
};

export interface DocumentSource {
  entry_id: number;
  original_order: number;
  original_content: string;
  current_content: string;
  has_diverged: boolean;
}

/**
 * The notes this document was built from, in the order they were used,
 * each saying whether it has changed since the draft was written.
 */
export const listDocumentSources = async (
  documentId: number
): Promise<DocumentSource[]> => {
  const response = await api.get(`/documents/${documentId}/source_entries/`);
  return response.data;
};

/**
 * Turn a storyboard into a draft document: board order becomes document
 * order, and each card's note leads the note it holds, marked as the
 * writer's own scaffolding. Replaces the create-draft-then-finalize
 * round trip, which dropped the card notes on the floor.
 */
export const createDocumentFromStoryboard = async (
  storyboardId: number,
  name?: string
): Promise<Document> => {
  const response = await api.post(`/storyboards/${storyboardId}/create-document/`,
    name ? { name } : {});
  return response.data;
};

export const deleteDocument = async (documentId: number): Promise<void> => {
  await api.delete(`/documents/${documentId}/`);
};

export const getDocumentSourceEntries = async (documentId: number): Promise<SourceEntry[]> => {
  const response = await api.get(`/documents/${documentId}/source_entries/`);
  return response.data;
};

// Document Folder API calls
export const createDocumentFolder = async (name: string, parentId: number | null = null): Promise<DocumentFolder> => {
  const response = await api.post('/document-folders/', {
    name: name,
    parent: parentId
  });
  return response.data;
};

export const listDocumentFolders = async (): Promise<DocumentFolder[]> => {
  const response = await api.get('/document-folders/');
  return response.data;
};

export const getDocumentFolder = async (folderId: number): Promise<DocumentFolder> => {
  const response = await api.get(`/document-folders/${folderId}/`);
  return response.data;
};

export const updateDocumentFolder = async (
  folderId: number,
  updates: { name?: string; parent?: number | null }
): Promise<DocumentFolder> => {
  // PATCH: partial update, so callers can send just { parent } or { name }
  const response = await api.patch(`/document-folders/${folderId}/`, updates);
  return response.data;
};

// Documents inside survive by default (they fall back to the top level).
// deleteDocuments also deletes everything in the folder's subtree.
export const deleteDocumentFolder = async (
  folderId: number,
  deleteDocuments = false
): Promise<void> => {
  await api.delete(`/document-folders/${folderId}/`, {
    params: deleteDocuments ? { delete_documents: 'true' } : undefined,
  });
};

export const moveDocumentToFolder = async (folderId: number, documentId: number): Promise<void> => {
  await api.post(`/document-folders/${folderId}/move_document/`, {
    document_id: documentId
  });
};

// Import API calls
export const importLocalFiles = async (files: File[]): Promise<Document[]> => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });
  
  const response = await api.post('/documents/import/local/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export interface GooglePickerConfig {
  client_id: string;
  api_key: string;
  app_id: string;
  // Account hint passed to Google so it can skip the account chooser
  email?: string;
}

export const getGooglePickerConfig = async (): Promise<GooglePickerConfig> => {
  const response = await api.get('/documents/import/google-picker/config/');
  return response.data;
};

export interface GoogleImportResult {
  imported: Array<{ id: number; name: string; google_doc_id: string }>;
  errors: Array<{ doc_id: string; error: string }>;
  total: number;
  success_count: number;
  error_count: number;
  // Google's image URLs are short-lived, so images are reported, not embedded
  images_skipped?: number;
}

// docIds come from the Google Picker; accessToken is the short-lived
// drive.file token the browser obtained, used once server-side to read
// just the picked files.
export const importGoogleDocs = async (
  docIds: string[],
  accessToken: string,
  // Names straight from the Picker, so the import doesn't depend on a
  // separate Drive metadata call for the title
  docNames: Record<string, string> = {}
): Promise<GoogleImportResult> => {
  const response = await api.post('/documents/import/google-docs/', {
    doc_ids: docIds,
    access_token: accessToken,
    doc_names: docNames,
  });
  return response.data;
};

export const getGoogleDocsAuthUrl = async (): Promise<{ authorization_url: string; state: string }> => {
  const response = await api.get('/documents/import/google-docs/auth/');
  return response.data;
};

export const listGoogleDocs = async (): Promise<{ documents: any[]; count: number }> => {
  const response = await api.get('/documents/import/google-docs/list/');
  return response.data;
};

export const authorizeGoogleDocs = async (): Promise<{ authUrl: string }> => {
  const response = await api.get('/documents/import/google-docs/auth/');
  return response.data;
};
